import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { NavLink } from 'react-router-dom';
import styled, { keyframes, css } from 'styled-components';

const orbit = keyframes`
  0% { transform: rotate(0deg) translateX(var(--orbit-radius)) rotate(0deg); }
  100% { transform: rotate(360deg) translateX(var(--orbit-radius)) rotate(-360deg); }
`;

const swarm = keyframes`
  0%, 100% {
    transform: translate(0, 0) scale(1);
    opacity: 0.8;
  }
  25% {
    transform: translate(30px, -20px) scale(1.2);
    opacity: 1;
  }
  50% {
    transform: translate(-20px, 30px) scale(0.8);
    opacity: 0.6;
  }
  75% {
    transform: translate(20px, 20px) scale(1.1);
    opacity: 0.9;
  }
`;

const hologram = keyframes`
  0%, 100% {
    box-shadow: 0 0 30px rgba(0, 255, 255, 0.5);
    filter: hue-rotate(0deg);
  }
  33% {
    box-shadow: 0 0 40px rgba(255, 0, 255, 0.7);
    filter: hue-rotate(120deg);
  }
  66% {
    box-shadow: 0 0 35px rgba(255, 255, 0, 0.6);
    filter: hue-rotate(240deg);
  }
`;

const NavContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  pointer-events: none;
  z-index: 10;
  overflow: hidden;
`;

const CentralHub = styled(motion.div)`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 120px;
  height: 120px;
  transform: translate(-50%, -50%);
  background: conic-gradient(from 0deg, #ff006e, #8338ec, #3a86ff, #06ffa5, #ffbe0b, #ff006e);
  border-radius: 50%;
  cursor: pointer;
  pointer-events: auto;
  animation: ${hologram} 4s infinite;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 20;

  &::before {
    content: '';
    position: absolute;
    top: 10px;
    left: 10px;
    right: 10px;
    bottom: 10px;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.2), transparent);
    border-radius: 50%;
  }

  @media (max-width: 768px) {
    width: 80px;
    height: 80px;
  }

  @media (max-width: 480px) {
    width: 60px;
    height: 60px;
  }
`;

const HubText = styled.span`
  color: white;
  font-family: 'Orbitron', monospace;
  font-weight: bold;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 2px;
  text-shadow: 0 0 10px rgba(255, 255, 255, 0.8);
  z-index: 1;

  @media (max-width: 768px) {
    font-size: 12px;
    letter-spacing: 1px;
  }

  @media (max-width: 480px) {
    font-size: 10px;
    letter-spacing: 1px;
  }
`;

const OrbitingNavItem = styled(motion.div)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform-origin: 0 0;
  pointer-events: auto;
  cursor: pointer;
  --orbit-radius: 180px;

  @media (max-width: 768px) {
    --orbit-radius: 140px;
  }

  @media (max-width: 480px) {
    --orbit-radius: 100px;
  }

  ${props => props.isActive && css`
    animation: ${orbit} 40s linear infinite;
    animation-delay: ${props.delay}s;
  `}
`;

const NavBubble = styled(motion.div)`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, ${props => props.color}40, ${props => props.color}80);
  border: 3px solid ${props => props.color};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  box-shadow: 0 0 25px ${props => props.color}60;
  position: relative;

  @media (max-width: 768px) {
    width: 60px;
    height: 60px;
    border: 2px solid ${props => props.color};
  }

  @media (max-width: 480px) {
    width: 45px;
    height: 45px;
    border: 2px solid ${props => props.color};
  }

  &::before {
    content: '';
    position: absolute;
    top: -5px;
    left: -5px;
    right: -5px;
    bottom: -5px;
    background: conic-gradient(from 0deg, ${props => props.color}, transparent, ${props => props.color});
    border-radius: 50%;
    z-index: -1;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover::before {
    opacity: 1;
  }
`;

const NavText = styled.span`
  color: white;
  font-family: 'Orbitron', monospace;
  font-weight: 600;
  font-size: 12px;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 1px;
  text-shadow: 0 0 8px rgba(255, 255, 255, 0.6);

  @media (max-width: 768px) {
    font-size: 10px;
    letter-spacing: 0.5px;
  }

  @media (max-width: 480px) {
    font-size: 8px;
    letter-spacing: 0.5px;
  }
`;

const ParticleSwarm = styled(motion.div)`
  position: absolute;
  width: 6px;
  height: 6px;
  background: ${props => props.color};
  border-radius: 50%;
  animation: ${swarm} ${props => props.duration}s infinite ease-in-out;
  animation-delay: ${props => props.delay}s;
  box-shadow: 0 0 12px ${props => props.color};
`;

const ModeToggle = styled(motion.button)`
  position: fixed;
  top: 20px;
  right: 20px;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  border: 3px solid #00ffff;
  background: linear-gradient(45deg, #ff006e, #8338ec);
  color: white;
  font-weight: bold;
  font-size: 12px;
  cursor: pointer;
  pointer-events: auto;
  z-index: 100;
  animation: ${hologram} 3s infinite;

  @media (max-width: 768px) {
    width: 50px;
    height: 50px;
    font-size: 10px;
    top: 15px;
    right: 15px;
  }

  @media (max-width: 480px) {
    width: 45px;
    height: 45px;
    font-size: 9px;
    top: 10px;
    right: 10px;
  }

  &:hover {
    transform: scale(1.1);
  }
`;

const Nav = () => {
  const [isActive, setIsActive] = useState(false);
  const [particles, setParticles] = useState([]);

  const navigationItems = [
    { name: 'Tasks', path: '/todo', color: '#ff006e', angle: 0 },
    { name: 'Blog', path: '/blog', color: '#8338ec', angle: 72 },
    { name: 'Work', path: '/work', color: '#3a86ff', angle: 144 },
    { name: 'About', path: '/about', color: '#06ffa5', angle: 216 },
    { name: 'Skills', path: '/skills', color: '#ffbe0b', angle: 288 },
  ];

  // Generate particle swarm
  useEffect(() => {
    if (isActive) {
      // Responsive particle count
      const isMobile = window.innerWidth <= 768;
      const particleCount = isMobile ? 15 : 30;

      const newParticles = Array.from({ length: particleCount }, (_, i) => ({
        id: i,
        x: Math.random() * window.innerWidth,
        y: Math.random() * window.innerHeight,
        color: ['#ff006e', '#8338ec', '#3a86ff', '#06ffa5', '#ffbe0b'][Math.floor(Math.random() * 5)],
        duration: 4 + Math.random() * 6,
        delay: Math.random() * 3,
      }));
      setParticles(newParticles);
    } else {
      setParticles([]);
    }
  }, [isActive]);

  return (
    <NavContainer>
      {/* Mode Toggle */}
      <ModeToggle
        onClick={() => setIsActive(!isActive)}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 1, type: "spring", stiffness: 200 }}
      >
        {isActive ? 'HIDE' : 'SHOW'}
      </ModeToggle>

      {/* Particle Swarm */}
      <AnimatePresence>
        {isActive && particles.map((particle) => (
          <ParticleSwarm
            key={particle.id}
            initial={{
              x: particle.x,
              y: particle.y,
              scale: 0,
              opacity: 0
            }}
            animate={{
              scale: 1,
              opacity: 0.8,
            }}
            exit={{
              scale: 0,
              opacity: 0
            }}
            transition={{
              duration: 0.5,
              delay: particle.delay,
            }}
            color={particle.color}
            duration={particle.duration}
            delay={particle.delay}
            style={{
              left: particle.x,
              top: particle.y,
            }}
          />
        ))}
      </AnimatePresence>

      {/* Central Hub */}
      <AnimatePresence>
        {isActive && (
          <CentralHub
            initial={{ scale: 0, rotate: 0 }}
            animate={{ scale: 1, rotate: 360 }}
            exit={{ scale: 0, rotate: -360 }}
            transition={{
              duration: 1,
              type: "spring",
              stiffness: 200,
              rotate: { duration: 2, repeat: Infinity, ease: "linear" }
            }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <HubText>NAV</HubText>
          </CentralHub>
        )}
      </AnimatePresence>

      {/* Orbiting Navigation Items */}
      <AnimatePresence>
        {isActive && navigationItems.map((item, index) => (
          <OrbitingNavItem
            key={item.name}
            isActive={isActive}
            delay={index * 2} // Increased delay to spread items out more
            initial={{
              scale: 0,
              rotate: item.angle,
            }}
            animate={{
              scale: 1,
              rotate: item.angle,
            }}
            exit={{
              scale: 0,
              rotate: item.angle - 360,
            }}
            transition={{
              duration: 0.8,
              delay: index * 0.15,
              type: "spring",
              stiffness: 200
            }}
          >
            <NavLink to={item.path} style={{ textDecoration: 'none' }}>
              <NavBubble
                color={item.color}
                whileHover={{
                  scale: 1.2,
                  boxShadow: `0 0 40px ${item.color}`,
                }}
                whileTap={{ scale: 0.9 }}
              >
                <NavText>{item.name}</NavText>
              </NavBubble>
            </NavLink>
          </OrbitingNavItem>
        ))}
      </AnimatePresence>

      {/* Background Effect */}
      <AnimatePresence>
        {isActive && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: 'radial-gradient(circle at 50% 50%, rgba(255, 0, 110, 0.1) 0%, transparent 40%), radial-gradient(circle at 30% 70%, rgba(58, 134, 255, 0.1) 0%, transparent 40%)',
              pointerEvents: 'none',
            }}
          />
        )}
      </AnimatePresence>
    </NavContainer>
  );
};

export default Nav;
